<template>
  <div class="users-management">
    <div class="users-management__header">
      <div class="users-management__actions">
        <SearchBar v-model="searchQuery" @search="applyFilters" @clear="clearSearch" />
        <UsersFiltersPanel
          :filters="filters"
          :accountOptions="accountOptions"
          @apply="applyFilters"
          @reset="resetFilters"
        />
         <el-popover
          v-model="statusPopoverVisible"
          placement="bottom-start"
          :width="160"
          trigger="click"
          style="margin-right: 2px;"
          :append-to-body="false"
        >
          <div class="status-popover">
            <p class="status-popover__label">Change status to:</p>
            <b-form-radio-group v-model="bulkStatusAction" stacked class="status-popover__options">
              <b-form-radio value="deactivate">Inactive</b-form-radio>
              <b-form-radio value="activate">Active</b-form-radio>
            </b-form-radio-group>
            <el-tooltip
              :content="'Select at least one user'"
              :disabled="selectedUsers.length > 0"
              placement="top"
            >
              <span class="status-popover__apply-wrapper">
                <button
                  class="status-popover__apply"
                  :disabled="selectedUsers.length === 0"
                  @click="applyBulkStatus"
                >Apply</button>
              </span>
            </el-tooltip>
          </div>
          <button
            slot="reference"
            class="change-status-button"
            v-b-popover.hover.top="'Updated Status'"
          >
            <i class="ion ion-toggle"></i>
          </button>
        </el-popover>
        <button
          class="container-download"
          v-b-popover.hover.top="'Download CSV'"
          :disabled="isDownloading"
          @click="handleDownload"
        >
          <b-spinner v-if="isDownloading" small variant="primary" />
          <i v-else class="el-icon-download"></i>
        </button>
      </div>
    </div>
    <b-alert :show="!!errorMsg" variant="danger" dismissible @dismissed="errorMsg = null">
      {{ errorMsg }}
    </b-alert>
    <div v-if="!loading && !errorMsg && users.length === 0" class="users-management__empty">
      <i class="ion ion-people-outline users-management__empty-icon"></i>
      <p>No users found.</p>
    </div>
    <div v-if="users.length > 0 || loading" class="users-management__table-container" ref="tableContainer">
      <UsersTable
        ref="usersTable"
        :tableData="users"
        :loading="loading"
        :isLoadingMore="isLoadingMore"
        :hasMoreData="hasMoreData"
        @toggleStatus="handleToggleStatus"
        @deleteUser="handleDeleteUser"
        @loadMoreItems="loadMoreUsers"
        @selectionChange="selectedUsers = $event"
      />
    </div>
  </div>
</template>

<script>
import swal from "sweetalert";
import { errorMessage, generalMessage, warningMessage } from "@/utils/ErrorMessages";
import downloadUsersCsv from "@/utils/exportUsersCsv";
import SearchBar from "./components/SearchBar.vue";
import UsersTable from "./components/UsersTable.vue";
import UsersFiltersPanel from "./components/UsersFiltersPanel.vue";

const initialFilters = () => ({
  name: "",
  lastName: "",
  email: "",
  accountName: "",
  status: "",
  role: "",
  lastConnection: "",
});

export default {
  name: "UsersManagement",
  components: { SearchBar, UsersTable, UsersFiltersPanel },
  data() {
    return {
      loading: false,
      isLoadingMore: false,
      hasMoreData: true,
      isDownloading: false,
      errorMsg: null,
      selectedUsers: [],
      statusPopoverVisible: false,
      bulkStatusAction: "deactivate",
      searchQuery: "",
      filters: initialFilters(),
      skip: 0,
      limit: 20,
      users: [],
    };
  },
  computed: {
    accountOptions() {
      const seen = new Set();
      return this.users
        .filter((u) => u.account?.id && u.account?.company_name)
        .reduce((acc, u) => {
          if (!seen.has(u.account.id)) {
            seen.add(u.account.id);
            acc.push({ id: u.account.id, name: u.account.company_name });
          }
          return acc;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  async created() {
    await this.loadUsers();
  },
  methods: {
    buildParams() {
      const params = {
        limit: this.limit,
        skip: this.skip,
      };
      if (this.searchQuery) params.search = this.searchQuery;
      if (this.filters.name) params.first_name = this.filters.name;
      if (this.filters.lastName) params.lastname = this.filters.lastName;
      if (this.filters.email) params.email = this.filters.email;
      if (this.filters.accountName) params.account_id = this.filters.accountName;
      if (this.filters.role) params.role_id = this.filters.role;
      if (this.filters.lastConnection) {
        params.last_connection_months = Number(this.filters.lastConnection);
      }
      if (this.filters.status === "active") params.is_active = true;
      if (this.filters.status === "inactive") params.is_active = false;
      return params;
    },

    async loadUsers(append = false) {
      if (append) {
        this.isLoadingMore = true;
      } else {
        this.loading = true;
      }
      this.errorMsg = null;
      try {
        const response = await this.$store.dispatch("user/getAllUsers", this.buildParams());
        if (response && !response.error) {
          const newUsers = Array.isArray(response) ? response : (response.data || response.users || []);
          this.users = append ? [...this.users, ...newUsers] : newUsers;
          if (newUsers.length < this.limit) {
            this.hasMoreData = false;
          }
        } else {
          this.errorMsg = "Error loading users. Please try again.";
        }
      } catch {
        this.errorMsg = "Unexpected error. Please try again.";
      } finally {
        if (append) {
          this.isLoadingMore = false;
        } else {
          this.loading = false;
        }
      }
    },

    async loadMoreUsers() {
      if (this.isLoadingMore || !this.hasMoreData) return;
      this.isLoadingMore = true;
      this.skip += this.limit;
      await this.loadUsers(true);
    },

    async applyFilters() {
      this.skip = 0;
      this.hasMoreData = true;
      this.users = [];
      await this.loadUsers();
    },

    async resetFilters() {
      this.filters = initialFilters();
      this.searchQuery = "";
      this.skip = 0;
      this.hasMoreData = true;
      this.users = [];
      await this.loadUsers();
    },

    async clearSearch() {
      this.searchQuery = "";
      this.skip = 0;
      this.hasMoreData = true;
      this.users = [];
      await this.loadUsers();
    },

    async handleToggleStatus({ id, status }) {
      const targetUser = this.users.find((u) => u.id === id);
      const storeAction = status ? "user/bulkActivateUsers" : "user/bulkDeactivateUsers";
      const actionLabel = status ? "activated" : "deactivated";
      try {
        await this.$store.dispatch(storeAction, [id]);
        await this.loadUsers();
        if (targetUser) {
          const name = [targetUser.first_name, targetUser.lastname].filter(Boolean).join(" ");
          generalMessage(`User ${actionLabel}: ${name} (${targetUser.email})`);
        }
      } catch {
        this.errorMsg = "Could not update user status. Please try again.";
      }
    },

    async handleDeleteUser(userId) {
      try {
        await this.$store.dispatch("user/bulkDeleteUsers", [userId]);
        this.users = this.users.filter((u) => u.id !== userId);
      } catch {
        this.errorMsg = "Could not delete user. Please try again.";
      }
    },

    applyBulkStatus() {
      this.statusPopoverVisible = false;
      this.handleBulkStatusChange(this.bulkStatusAction);
    },

    async handleBulkStatusChange(action) {
      if (action === "deactivate") {
        const gltAdmins = this.selectedUsers.filter((u) => u.role?.id === 1);
        if (gltAdmins.length > 0) {
          const names = gltAdmins.map((u) => [u.first_name, u.lastname].filter(Boolean).join(" ")).join(", ");
          warningMessage("GLT Admin users cannot be deactivated.", `Please deselect: ${names}`);
          return;
        }
      }
      const isDeactivate = action === "deactivate";
      const actionLabel = isDeactivate ? "deactivated" : "activated";
      const titleLabel = isDeactivate ? "Deactivate users?" : "Activate users?";
      const btnLabel = isDeactivate ? "Deactivate" : "Activate";

      const list = document.createElement("ul");
      list.style.cssText = "text-align:left;padding-left:20px;margin:8px 0 0;max-height:200px;overflow-y:auto;font-size:13px;";
      this.selectedUsers.forEach((u) => {
        const li = document.createElement("li");
        li.textContent = `${[u.first_name, u.lastname].filter(Boolean).join(" ")} (${u.email})`;
        list.appendChild(li);
      });

      const confirmed = await swal({
        title: titleLabel,
        text: `The following ${this.selectedUsers.length} user(s) will be ${actionLabel}:`,
        content: list,
        icon: "warning",
        buttons: ["Cancel", btnLabel],
        dangerMode: isDeactivate,
      });

      if (!confirmed) return;

      const ids = this.selectedUsers.map((u) => u.id);
      const names = this.selectedUsers
        .map((u) => [u.first_name, u.lastname].filter(Boolean).join(" "))
        .join(", ");
      const storeAction = isDeactivate ? "user/bulkDeactivateUsers" : "user/bulkActivateUsers";
      try {
        await this.$store.dispatch(storeAction, ids);
        this.selectedUsers = [];
        this.$refs.usersTable.clearSelection();
        await this.loadUsers();
        generalMessage(`${ids.length} user${ids.length > 1 ? "s" : ""} ${actionLabel}: ${names}`);
      } catch {
        this.errorMsg = `Could not ${action} users. Please try again.`;
      }
    },

    async handleDownload() {
      this.isDownloading = true;
      try {
        const { limit, skip, ...exportParams } = this.buildParams();
        const fullData = await this.$store.dispatch("user/downloadUsersSSE", exportParams);
        if (!fullData) return; // store already showed the error swal
        if (fullData.length === 0) {
          warningMessage("No users found to export.");
          return;
        }
        downloadUsersCsv(fullData);
        generalMessage("Users exported successfully.");
      } catch {
        errorMessage("Download failed. Please try again.");
      } finally {
        this.isDownloading = false;
      }
    },

  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/_variables" as *;
@use "@/assets/scss/_mixin" as *;

::v-deep .popper__arrow {
  display: none !important;
}
// ── el-popover wrapper override ───────────────────────────────
::v-deep .el-popover {
  border: 2px solid var(--color-border-container) !important;
  border-radius: 5px !important;
  padding: 8px !important;
  box-shadow: 0 8px 24px rgba(13, 27, 62, 0.12) !important;
}

.status-popover {
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: $font-family-portal, sans-serif;
  background: #efefef;
  padding: 8px;

  &__label {
    margin: 0;
    font-size: 11px;
    font-family: $font-family-portal, sans-serif;
    color: #606266;

  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 10px;
    border-radius: 5px;
    padding: 8px;
    ::v-deep .custom-control {
      display: flex;
      align-items: center;
      min-height: unset;
      padding-left: 1.6rem;
    }

    ::v-deep .custom-control-label {
      font-size: 11px;
      font-family: $font-family-portal, sans-serif;
      line-height: 1;
      cursor: pointer;

      &::before,
      &::after {
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: $color-border-container;
    border-color: $color-border-container;
  }

  &__apply-wrapper {
    align-self: center;
    display: inline-block;
  }

  &__apply {
    border-radius: 15px;
    border: 1px solid $color-border-container;
    background-color: #fff;
    color: $color-border-container;
    font-family: $font-family-portal, sans-serif;
    font-weight: 700;
    height: 26px;
    width: 72px;
    font-size: 11px;
    cursor: pointer;
    transition: background-color 0.18s ease, color 0.18s ease;

    &:hover:not(:disabled) {
      background-color: $color-border-container;
      color: #fff;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
}

.users-management {
  @include splitted-view-styles;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 54px);
  overflow: hidden;
  padding: 12px 24px 0;
  width: 100%;
  font-family: $font-family-portal, sans-serif;
  box-sizing: border-box;

  &__header {
    flex-shrink: 0;
    display: flex;
    justify-content: end;
    align-items: center;
    margin: 10px;
    gap: 10px;
    position: relative;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  // ── Bulk deactivate button ────────────────────────────────────
  .container-bulk-deactivate {
    border-radius: 5px;
    border: 2px solid #d32f2f;
    color: #d32f2f;
    background-color: #efefef;
    padding: 0.1rem 0.4rem;
    font-weight: 600;
    font-size: 17px;
    cursor: pointer;

    &:hover {
      border-color:#d32f2f;
      background-color: rgba(0, 185, 204, 0.06);
    }
  }
  .change-status-button{
    border-radius: 5px;
    border: 2px solid var(--color-border-container);
    color: $color-primary-company;
    background-color: #efefef;
    padding: 0.13rem 0.43rem;
    font-weight: 600;
    font-size: 17px;
    cursor: pointer;

    &:hover {
      border-color: var(--color-border-container);
      background-color: rgba(0, 185, 204, 0.06);
    }
  }

  // ── Download button — mismo estilo que Account Management ────
  .container-download {
    border-radius: 5px;
    border: 2px solid var(--color-border-container);
    color: $color-primary-company;
    background-color: #efefef;
    padding: 0.13rem 0.43rem;
    font-weight: 600;
    font-size: 17px;
    cursor: pointer;

    &:hover {
      border-color: var(--color-border-container);
      background-color: rgba(0, 185, 204, 0.06);
    }
  }

  // ── Empty state ──────────────────────────────────────────────
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    color: #9aa5be;
    font-family: $font-family-portal, sans-serif;
    font-size: 14px;
  }

  &__empty-icon {
    font-size: 52px;
    margin-bottom: 10px;
  }

  // ── Table container ──────────────────────────────────────────
  &__table-container {
    flex: 1;
    min-height: 0;
    background-color: #ffffff;
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(13, 27, 62, 0.05);
    overflow: hidden;
  }
}
::v-deep .el-popover {
  font-size: 11px;
  font-family: $font-family-portal, sans-serif;
  color: #606266;
  font-weight: 400;
}
</style>
